import { faFileCircleInfo as farFileCircleInfo } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faArrowTurnDown as fasArrowTurnDown,
	faCircleQuestion as fasCircleQuestion,
	faClockRotateLeft as fasClockRotateLeft,
	faLightbulbExclamationOn as fasLightbulbExclamationOn,
	faListUl as fasListUl,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type { AbstractLeadFormProps } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractResultItemProps } from "@component/assets/AbstractListingCalculator/AbstractResultItem";
import { AbstractResultItem } from "@component/assets/AbstractListingCalculator/AbstractResultItem";
import { DataBox } from "@component/assets/AbstractListingCalculator/components/DataBox";
import { MarketingAccordion } from "@component/assets/AbstractListingCalculator/components/MarketingAccordion";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import emagImg from "@img/landing/lakashitel/emag-logo.png";
import { ExpertBlockquote } from "@ui/components/ExpertBlockquote";
import { formatText } from "@ui/format";
import { faBankmonitor } from "@ui/fortawesome/bankmonitor";
import { BmBadge, Button, HtmlBlock, InfoPopup, List, __, addParameterToURL, getLocale } from "bm-ui-kit";
import compact from "lodash/compact";
import lzString from "lz-string";
import React from "react";
import type { ReactNode } from "react";
import { Col, Row } from "react-bootstrap";
import { MortgageLeadForm } from "../LeadForm/MortgageLeadForm";
import type { MortgageLeadCalcRequest, MortgageLeadCalcResponse } from "../LeadForm/types";
import { getInterestCategory, getLeadCalculatorRequest, getLeadCalculatorResponse } from "./domain";
import type { MortgageRequest, MortgageResponseItem, MortgageSearchParams } from "./types";

export interface Props extends AbstractResultItemProps<MortgageSearchParams, MortgageRequest, MortgageResponseItem> {
	onlyCt?: boolean;
	variant?: string;
}

interface BoxData {
	text: ReactNode;
	value: ReactNode;
}

export class MortgageResultItem extends AbstractResultItem<
	MortgageSearchParams,
	MortgageRequest,
	MortgageResponseItem,
	Props
> {
	protected readonly hasDetailsBlock: boolean = false;

	protected renderProductData(): ReactNode {
		const {
			result,
			searchParams: { sort = "THM" },
			variant,
		} = this.props;

		const extraClasses = result.gracePeriodInstallment ? "col-6 " : "col-4 ";
		const dataBoxClass = `${variant === undefined ? extraClasses : ""}col-md-3`;
		const dataPageUrl = this.getFullDataPageUrl();

		return (
			<>
				{this.rendersStartInstallmentDataBox(
					result,
					compact([dataBoxClass, sort === "INDULO_TORLESZTES" && "ordered-by"]).join(" "),
				)}
				<DataBox
					className={dataBoxClass}
					orderBy={sort === "THM"}
					title={__("THM")}
					value={formatText(result.apr, "%")}
				/>
				<DataBox
					className={`${dataBoxClass} d-none d-md-block`}
					orderBy={sort === "OSSZES_TORLESZTES"}
					title={
						<>
							<span className="d-none d-md-inline">{__("teljes visszafizetés")}</span>
							<span className="d-md-none">{__("visszafizetés")}</span>

							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
						</>
					}
					value={formatText(result.fullPayableAmount, "ft")}
					info={__("A teljes visszafizetés tartalmazza a törlesztőrészleteket és a költségeket.")}
				/>
				{this.renderLastData(dataBoxClass)}
				<div className="text-center bg-white w-100 small py-2 d-md-none">
					<a
						rel="nofollow"
						href={this.hasDetailsBlock ? undefined : dataPageUrl}
						// biome-ignore lint/a11y/noBlankTarget: <explanation>
						target="_blank"
						onClick={this.hasDetailsBlock ? this.onDetailsClick : this.onProductPageClick}
					>
						<FontAwesomeIcon icon={farFileCircleInfo} className="mx-1" />
						<span className="text-underline">termék további részletei</span>
					</a>
				</div>
			</>
		);
	}

	private rendersStartInstallmentDataBox(result: MortgageResponseItem, className: string): ReactNode {
		const {
			request: { sort = "THM" },
			searchParams: { maturity },
			variant,
		} = this.props;
		const orderBy = sort === "INDULO_TORLESZTES";

		if (result.gracePeriodInstallment || result.discountedPeriodInstallment) {
			const boxes: BoxData[] = [
				{
					text: (
						<InfoPopup
							tooltip={
								result.discountedPeriodInstallment
									? "A türelmi időszakot és kedvezményes időszakot követő első normál kamattal számolt tőkét és kamatot is tartalmazó törlesztőrészlet."
									: "A 13. hónaptól érvényes törlesztőrészlet, amely már tőkét és kamatot is tartalmaz."
							}
						>
							havi törlesztő
							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
						</InfoPopup>
					),
					value:
						maturity <= 5 && !!result.discountedPeriodInstallment
							? "-"
							: formatText(result.installmentStart, "ft"),
				},
			];

			if (result.discountedPeriodInstallment) {
				boxes.unshift({
					text: (
						<InfoPopup tooltip="A kedvezményes időszak alatt fizetendő teljes, nem türelmi idős törlesztőrészlet.">
							kedvezményes törlesztő
							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
						</InfoPopup>
					),
					value: formatText(result.discountedPeriodInstallment, "ft"),
				});
			}

			if (result.gracePeriodInstallment) {
				boxes.unshift({
					text: (
						<InfoPopup
							tooltip={
								result.discountedPeriodInstallment
									? "A türelmi idő alatt megfizetett, csak kamatot tartalmazó törlesztőrészlet."
									: "A türelmi idő alatt (az első 12 hónapban) a törlesztőrészlet csak kamatfizetésből áll, tőketörlesztés nincs."
							}
						>
							türelmi idős törlesztő
							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
						</InfoPopup>
					),
					value: formatText(result.gracePeriodInstallment, "ft"),
				});
			}

			return this.renderDoubleDataBox(boxes, className, orderBy);
		}

		return (
			<DataBox
				className={className}
				orderBy={orderBy}
				title={
					<>
						<span className="d-none d-md-inline">{__("havi törlesztőrészlet")}</span>
						<span className="d-md-none">{__("havi törlesztő")}</span>

						{variant === undefined && !result.marketing?.newTargetUrl && (
							<Button
								className=" d-none d-lg-flex p-1 text-underlined"
								variant="link"
								icon={
									<FontAwesomeIcon
										icon={fasLightbulbExclamationOn}
										className="text-success"
										size="lg"
									/>
								}
								onClick={() => this.onInterestClick("reduce-payment")}
							>
								Csökkentsd a törlesztőt
							</Button>
						)}
					</>
				}
				value={
					<>
						{formatText(result.installmentStart, "ft")}
						{variant === undefined && !result.marketing?.newTargetUrl && (
							<Button
								className="d-none d-md-block d-lg-none p-1 text-underlined"
								size="sm"
								variant="link"
								icon={
									<FontAwesomeIcon
										icon={fasLightbulbExclamationOn}
										className="text-success"
										size="lg"
									/>
								}
								onClick={() => this.onInterestClick("reduce-payment")}
							>
								Csökkentsd a törlesztőt
							</Button>
						)}
					</>
				}
			/>
		);
	}

	private renderDoubleDataBox(boxes: BoxData[], colClasses: string, highlight = false): ReactNode {
		return (
			<>
				{/* desktop */}
				<div
					className={`data-box text-center flex-column justify-content-center align-items-center d-none d-md-flex py-lg-3 py-sm-2 py-1 px-sm-3 px-2 ${colClasses}`}
				>
					<div className="data-description d-block w-100 text-sm-center">
						{boxes.flatMap((box, index) => [
							<strong key={`${index}-v`}>{box.value}</strong>,
							<div key={`${index}-t`}>{box.text}</div>,
						])}
					</div>
				</div>
				{/* mobil */}
				{boxes.map((box, index) => (
					<DataBox
						key={index}
						className="col-sm-6 d-md-none horizontal-data-box"
						orderBy={highlight}
						title={box.text}
						value={<div className="ml-2">{box.value}</div>}
						contentClassName="justify-content-between py-2"
					/>
				))}
			</>
		);
	}

	private renderLastData(dataBoxClass: string): ReactNode {
		const { result } = this.props;

		if (result.marketing?.highlight || !Config.showBmDiscounts()) {
			return <DataBox className={dataBoxClass} title="fix kamat" value={this.getInterestPeriod()} />;
		}

		if (!result.bmDiscounts || !result.bmDiscounts.totalSavings || result.marketing?.newTargetUrl) {
			return (
				<div
					className={`col-md-3 result-data-box justify-content-center align-items-center ${dataBoxClass} d-flex`}
				>
					<FontAwesomeIcon icon={faBankmonitor} className="text-light" size="3x" />
				</div>
			);
		}

		return (
			<DataBox
				className={`bm-discount total-savings-result-box py-1 ${dataBoxClass}`}
				title={
					<>
						<span className="d-none d-md-inline">{__("Bankmonitor kedvezmény")}</span>
						<span className="d-md-none">
							<FontAwesomeIcon icon={faBankmonitor} className="text-primary" /> kedvezmény
						</span>
						<span className="d-none d-md-inline">
							<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
						</span>
					</>
				}
				value={
					<div className="ml-2 ml-md-0">
						{formatText(result.bmDiscounts.totalSavings, "ft")}
						<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1 d-md-none text-dark" />
					</div>
				}
				info={__(
					"Az alábbi kedvezményeket akkor veheted igénybe, ha rajtunk keresztül igényled a lakáshitelt. További információért kattints a RÉSZLETEK gombra!",
				)}
				contentClassName="justify-content-end"
			/>
		);
	}

	protected renderButtons(): ReactNode {
		const { variant, result, selected } = this.props;
		const { feedBackStatus } = this.state;

		const dataPageUrl = this.getFullDataPageUrl();

		if (variant === undefined && !result.marketing?.newTargetUrl) {
			return (
				<div className="buttons p-2 ml-auto d-flex align-items-center">
					<div className="border-right text-right fs-sm pr-2 d-md-none">
						<strong>Csökkentsd a törlesztőt</strong>
						<br />
						szakértőnkkel!
					</div>
					<FontAwesomeIcon
						icon={fasArrowTurnDown}
						className="text-primary mx-2 mb-3 d-md-none"
						rotation={270}
					/>
					<Button
						variant="light"
						className="d-none d-md-inline-block mx-2"
						icon={<FontAwesomeIcon icon={fasListUl} />}
						rel="nofollow"
						href={this.hasDetailsBlock ? undefined : dataPageUrl}
						target="_blank"
						active={selected && feedBackStatus === "details"}
						onClick={this.hasDetailsBlock ? this.onDetailsClick : this.onProductPageClick}
					>
						{__("Részletek")}
					</Button>
					<Button
						variant="success"
						className="font-weight-bold text-uppercase ml-0"
						active={selected && feedBackStatus === "interest"}
						onClick={() => this.onInterestClick()}
					>
						{__("Érdekel")}
					</Button>
				</div>
			);
		}

		return super.renderButtons();
	}

	protected renderFeedback(): ReactNode {
		const { feedBackStatus } = this.state;
		const { result } = this.props;

		if (!feedBackStatus) {
			return;
		}

		return (
			<Row id={`feedback-${result.id}`} className="feedback mx-0">
				{feedBackStatus === "details" && (
					<Col lg={7} className="details block py-3 order-lg-1">
						{this.renderFeedbackDetails()}
					</Col>
				)}
				{feedBackStatus === "interest" && (
					<Col lg={7} className="interest block py-3 order-lg-1">
						{this.renderFeedbackInterest()}
					</Col>
				)}
				<Col lg={5} className="lead-block bg-success p-0 order-lg-2">
					{this.renderFeedbackLead(this.getLeadProps())}
				</Col>
			</Row>
		);
	}

	protected renderFeedbackLead(
		props: AbstractLeadFormProps<MortgageLeadCalcRequest, MortgageLeadCalcResponse, MortgageSearchParams>,
	): ReactNode {
		const { searchParams, result } = this.props;

		return (
			<MortgageLeadForm
				{...props}
				title={__("Kérj ingyenes segítséget szakértőnktől!")}
				request={getLeadCalculatorRequest(searchParams)}
				response={getLeadCalculatorResponse(result)}
			/>
		);
	}

	protected renderFeedbackInterest(): ReactNode {
		const {
			result: { installmentStart },
			variant,
		} = this.props;

		if (variant === undefined) {
			return (
				<>
					<div className="display-4 display-bold text-primary">
						Szakértőink díjmentesen segítenek csökkenteni a {formatText(installmentStart, "ft")} törlesztőt!
					</div>
					<List itemStyle="tick" variant="primary" className="ml-2">
						<List.Item>
							<HtmlBlock
								as="span"
								html={__(
									"Megvizsgálunk minden <strong>elérhető támogatást</strong> és <strong>támogatott hitellehetőséget.</strong>",
								)}
							/>
						</List.Item>
						<List.Item>
							<HtmlBlock
								as="span"
								html={__(
									"Megkeressük azt a <strong>futamidőt</strong>, amely mellett a <strong>törlesztő a legkedvezőbb</strong> és a teljes visszafizetés sem ugrik meg jelentősen.",
								)}
							/>
						</List.Item>
						<List.Item>
							<HtmlBlock
								as="span"
								html={__(
									"Folyamatosan nyomon követjük helyetted a piacot és szólunk, ha <strong>meglévő kölcsönöd kedvezőbbre cserélheted!</strong>",
								)}
							/>
						</List.Item>
					</List>
					<ExpertBlockquote
						expertId="nagy"
						quote="Segítek! Kérd ingyenes személyre szabott ajánlatodat!"
						title="lakáshitel szakértő"
					/>
				</>
			);
		}

		return (
			<>
				<div className="h4">{__("Miért jársz velünk jobban, mint a bankfiókban?")}</div>
				<List itemStyle="tick" variant="primary" className="ml-2">
					<List.Item>
						<HtmlBlock
							as="span"
							html={__("24 órán belül megtudhatod, <strong>mennyi lakáshitelt</strong> kaphatsz.")}
						/>
					</List.Item>
					<List.Item>
						<HtmlBlock
							as="span"
							html={__(
								"Megoldást kínálunk a lakáshitel felvétel <strong>legbonyolultabb eseteiben</strong> is.",
							)}
						/>
					</List.Item>
					<List.Item>
						<HtmlBlock
							as="span"
							html={__(
								"<strong>Ingyenes szolgáltatást</strong>, egyedi kedvezményeket, gyors ügyintézést biztosítunk.",
							)}
						/>
					</List.Item>
					<List.Item>
						<HtmlBlock as="span" html={__("<strong>Független szakértői csapat</strong> támogat.")} />
					</List.Item>
					<List.Item>
						<HtmlBlock
							as="span"
							html={__("<strong>Ugyanazon kondíciók</strong>&nbsp;mint a bankfiókban.")}
						/>
					</List.Item>
					<List.Item>
						<HtmlBlock
							as="span"
							html={__("Bankfióki <strong>sorban állás nélkül</strong>, gyors hitelügyintézés.")}
						/>
					</List.Item>
				</List>
				<ExpertBlockquote
					expertId="nagy"
					quote="Segítek! Kérd ingyenes személyre szabott ajánlatodat!"
					title="lakáshitel szakértő"
				/>
			</>
		);
	}

	protected renderFeedbackDetails(): ReactNode {
		return null;
	}

	protected renderMarketingData(): ReactNode {
		if (getLocale() !== "hu_HU") {
			return null;
		}

		const {
			result: { marketing, headId, bmDiscounts, id },
		} = this.props;

		if (this.isDigiJzhProduct()) {
			return (
				<div className="p-2">
					<BmBadge
						prependAdditionalClasses="bg-white"
						size="lg"
						prepend={<img src={emagImg} className="badge-img" alt="eMAG logo" />}
					>
						az online előminősítés most 50 ezer ft eMAG utalványt érhet.
					</BmBadge>
				</div>
			);
		}

		// egyedi marketing szöveg
		if (
			(headId === 1177 && id.endsWith("-120") && bmDiscounts?.interestDiscount > 0) ||
			(headId === 1475 && id.endsWith("-120") && bmDiscounts?.interestDiscount > 0)
		) {
			return (
				<HtmlBlock
					className="marketing-text font-weight-light m-2"
					html={
						'<ul class="list-style-tick list-success list-sm list-inline mb-0"><li>Bankfiókban nem elérhető!</li><li>Bankmonitor kedvezmény</li></ul>'
					}
				/>
			);
		}

		if (!marketing?.formattedText) {
			return null;
		}

		if (marketing?.highlight) {
			return (
				<MarketingAccordion colProps={{ xl: 9, md: 8 }}>
					<HtmlBlock className="marketing-text font-weight-light my-2" html={marketing?.formattedText} />
				</MarketingAccordion>
			);
		}

		return null;
	}

	private isBmOnlyInterestDiscount(): boolean {
		const { result } = this.props;

		return result?.marketing?.cssRule?.includes("bm-only-interest-discount");
	}

	private isDigiJzhProduct(): boolean {
		const { result } = this.props;
		const bankCodes = ["otp", "cib", "kh", "erste"];

		return (
			result?.bank?.name &&
			bankCodes.some((bankCode) => result.bank.code === bankCode && !result.marketing.highlight)
		);
	}

	protected renderBmPromo(): ReactNode {
		if (this.isBmOnlyInterestDiscount()) {
			return <div className="bm-only-interest-discount-badge">Egyedi elbírálású ajánlat</div>;
		}

		if (this.isDigiJzhProduct()) {
			return (
				<div className="digijzh-promo">
					<FontAwesomeIcon icon={fasClockRotateLeft} className="mr-1" />
					<span className="d-none d-sm-inline">
						Előminősítés <strong>24 órán</strong> belül teljesen online!
					</span>
				</div>
			);
		}
	}

	protected getDataPageUrl(): string {
		const { result, searchParams } = this.props;

		return addParameterToURL(`/lakashitel-kalkulator/adatlap/`, {
			ID: result.id,
			d: lzString.compressToEncodedURIComponent(JSON.stringify(searchParams)),
			tab: "MORTGAGE",
		});
	}

	private getInterestPeriod(): string {
		const {
			searchParams: { maturity },
			result,
		} = this.props;

		const maturityMonths = maturity * 12;
		let interestPeriod;
		let interestPeriodObject = result.interestPeriodNotAided;

		if (result.interestPeriodAided?.month) {
			interestPeriodObject = result.interestPeriodAided;
		}

		if (maturityMonths <= Number(interestPeriodObject.month)) {
			interestPeriod = "Végig";
		} else {
			interestPeriod = interestPeriodObject.name;
		}

		if (result.loanType === "stateSponsoredLoanHome" && result.grantYears === 25) {
			interestPeriod = "Végig";
		}

		if (interestPeriod !== "Végig") {
			interestPeriod = `${interestPeriodObject.name}ig`;
		}

		return interestPeriod;
	}
	protected getResultClassName(): string {
		const {
			searchParams: { maturity },
			result,
		} = this.props;
		const colorClass = `${getInterestCategory(maturity, result)}-interest`;

		return `${super.getResultClassName()} ${colorClass}`;
	}

	protected getComponentName(): string {
		return "MortgageResultItem";
	}
}
